/**
 * iter 73 — Customer "Submit a Job" page.
 *
 * Previously this page rendered raw F1 inputs for the service address — a
 * divergence from the unified Location architecture used by Locations.jsx,
 * Cases.jsx and Jobs.jsx. This refactor brings it in line:
 *
 *   • Customer orgs now keep their own reusable "service location" address
 *     book (scoped to /api/locations under their own org_id).
 *   • Submitting a job lets the customer pick an existing saved location
 *     OR open the canonical LocationManagerDrawer (locationKind="service")
 *     to create a new one — exact same UX as Jobs.jsx on the host side.
 *   • The picked location's snapshot (address1/2/city/state/zip/lat/lng/
 *     driver-notes) is sent to POST /customer/jobs which copies it into a
 *     host-side `locations` row so the host org owns the FK target.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { Page } from "../components/AppShell";
import { Briefcase, CheckCircle, Buildings, MapPin, Plus, PencilSimple } from "@phosphor-icons/react";
import LocationManagerDrawer from "../components/OrgAdmin/LocationManagerDrawer";

const F = { case_number: "", plaintiff: "", defendant: "", court: "", judge: "",
            service_location_id: "",
            address1: "", address2: "", city: "", state: "", postal_code: "",
            latitude: null, longitude: null,
            location_name: "", location_notes: "",
            subject_first_name: "", subject_last_name: "", subject_dob: "",
            priority: "normal", due_date: "", notes: "", host_org_id: "" };

export default function SubmitJob() {
  const nav = useNavigate();
  const [hosts, setHosts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState(F);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(null);
  const [locDrawerOpen, setLocDrawerOpen] = useState(false);

  useEffect(() => {
    api.get("/customer/hosts").then((r) => {
      setHosts(r.data);
      if (r.data.length === 1) setForm((f) => ({ ...f, host_org_id: r.data[0].id }));
    }).catch((e) => setErr(formatErr(e)));
    api.get("/locations").then((r) => setLocations(r.data || [])).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickLocation = (id) => {
    const loc = locations.find((l) => l.id === id);
    if (!loc) {
      setForm((f) => ({ ...f, service_location_id: "" }));
      return;
    }
    setForm((f) => ({
      ...f,
      service_location_id: loc.id,
      location_name: loc.name || "",
      address1: loc.address1 || "",
      address2: loc.address2 || "",
      city: loc.city || "",
      state: loc.state || "",
      postal_code: loc.postal_code || "",
      latitude: loc.latitude ?? null,
      longitude: loc.longitude ?? null,
      location_notes: loc.notes || "",
    }));
  };

  const onLocationSaved = async (created) => {
    try {
      const r = await api.get("/locations");
      setLocations(r.data || []);
    } catch { /* ignore */ }
    if (created?.id) pickLocation(created.id);
  };

  const submit = async () => {
    if (!form.host_org_id) { setErr("Pick a host org."); return; }
    if (!form.address1)    { setErr("Pick or add a service location."); return; }
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        service_location_id: form.service_location_id || null,
      };
      const r = await api.post("/customer/jobs", payload);
      setOk(r.data);
      setTimeout(() => nav("/my-jobs"), 1200);
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (ok) {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="submit-job-success">
        <div className="surface p-12 text-center">
          <CheckCircle size={48} weight="thin" className="mx-auto text-green-600 mb-3" />
          <h2 className="text-xl font-semibold mb-1">Submitted!</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Job <span className="mono">{ok.job_number}</span> is now pending acknowledgement by the host org.
          </p>
          <p className="text-xs text-muted-foreground">Taking you to MY JOBS…</p>
        </div>
      </div>
    );
  }

  const hasAddress = !!form.address1;

  return (
    <Page
      title="Submit a Job"
      subtitle="SEND A NEW JOB TO A HOST ORG FOR PROCESSING"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Submit a Job"]}
      testid="submit-job-page"
    >
      <div className="max-w-3xl mx-auto">

      <Section title="HOST ORG" icon={Buildings} testid="sec-host">
        <select className="input" value={form.host_org_id} onChange={set("host_org_id")} data-testid="submit-host-org">
          <option value="">— pick a host org —</option>
          {hosts.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
        {hosts.length === 0 && <div className="text-xs text-muted-foreground mt-2">No host orgs in your collaborating list. Ask your contact to add you.</div>}
      </Section>

      <Section title="CASE" icon={Briefcase} testid="sec-case">
        <Row><F1 label="CASE NUMBER" v={form.case_number} onChange={set("case_number")} testid="case-number" />
             <F1 label="COURT" v={form.court} onChange={set("court")} testid="court" /></Row>
        <Row><F1 label="PLAINTIFF" v={form.plaintiff} onChange={set("plaintiff")} testid="plaintiff" />
             <F1 label="DEFENDANT" v={form.defendant} onChange={set("defendant")} testid="defendant" /></Row>
        <F1 label="JUDGE" v={form.judge} onChange={set("judge")} testid="judge" />
      </Section>

      <Section title="SUBJECT" icon={Briefcase} testid="sec-subject">
        <Row><F1 label="FIRST NAME" v={form.subject_first_name} onChange={set("subject_first_name")} testid="subject-first" />
             <F1 label="LAST NAME" v={form.subject_last_name} onChange={set("subject_last_name")} testid="subject-last" /></Row>
        <F1 label="DATE OF BIRTH" v={form.subject_dob} onChange={set("subject_dob")} testid="subject-dob" type="date" />
      </Section>

      <Section title="SERVICE LOCATION" icon={MapPin} testid="sec-location">
        <div className="flex gap-2 mb-3">
          <select
            className="input flex-1"
            value={form.service_location_id}
            onChange={(e) => pickLocation(e.target.value)}
            data-testid="service-location-picker"
          >
            <option value="">— pick a saved location —</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}{l.address1 ? ` · ${l.address1}` : ""}{l.city ? `, ${l.city}` : ""}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setLocDrawerOpen(true)}
            aria-label="Add new service location"
            data-testid="add-service-location-btn"
          >
            <Plus size={14} /> NEW
          </button>
        </div>

        {hasAddress ? (
          <div className="surface p-3 bg-muted/30" data-testid="service-location-preview">
            <div className="flex items-start gap-2">
              <MapPin size={14} className="mt-0.5 text-primary" weight="duotone" />
              <div className="flex-1 text-sm">
                {form.location_name && (
                  <div className="font-medium">{form.location_name}</div>
                )}
                <div>{form.address1}{form.address2 ? `, ${form.address2}` : ""}</div>
                <div className="text-muted-foreground">
                  {[form.city, form.state, form.postal_code].filter(Boolean).join(", ")}
                </div>
                {form.location_notes && (
                  <div className="mt-2 text-[11px] italic text-muted-foreground">
                    Driver notes: {form.location_notes}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="btn btn-ghost text-xs"
                onClick={() => setLocDrawerOpen(true)}
                aria-label="Edit selected service location"
                data-testid="edit-service-location-btn"
              >
                <PencilSimple size={12} /> EDIT
              </button>
            </div>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            Pick from your saved locations or click <strong>+ NEW</strong> to add one (Google Places autocomplete, lat/lng, driver-facing notes).
          </div>
        )}
      </Section>

      <Section title="JOB DETAILS" icon={Briefcase} testid="sec-details">
        <Row>
          <div className="py-1 flex-1"><div className="label-xs mb-0.5">PRIORITY</div>
            <select className="input" value={form.priority} onChange={set("priority")} data-testid="priority">
              <option value="normal">Normal</option><option value="rush">Rush</option><option value="emergency">Emergency</option>
            </select></div>
          <F1 label="DUE DATE" v={form.due_date} onChange={set("due_date")} testid="due-date" type="datetime-local" />
        </Row>
        <div className="py-1"><div className="label-xs mb-0.5">NOTES / SPECIAL INSTRUCTIONS</div>
          <textarea className="input min-h-[80px]" value={form.notes} onChange={set("notes")} data-testid="notes" /></div>
      </Section>

      {err && <div className="text-xs text-destructive mb-3" data-testid="submit-error">{err}</div>}

      <button onClick={submit} disabled={busy} className="btn btn-primary w-full h-11 mt-2" data-testid="submit-job-btn">
        {busy ? "SUBMITTING…" : "SUBMIT JOB"}
      </button>
      </div>

      {/* Unified service-location drawer — same component used by Jobs.jsx
          and Locations.jsx. Saves to the customer's OWN /api/locations
          address book so future submissions can reuse it. */}
      <LocationManagerDrawer
        open={locDrawerOpen}
        onClose={() => setLocDrawerOpen(false)}
        onSuccess={onLocationSaved}
        mode={form.service_location_id ? "edit" : "create"}
        initialData={form.service_location_id ? locations.find((l) => l.id === form.service_location_id) : null}
        locationKind="service"
      />
    </Page>
  );
}

const Section = ({ title, icon: I, children, testid }) => (
  <div className="surface p-4 mb-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3"><I size={14} /><span className="label-xs">{title}</span></div>
    {children}
  </div>
);
const Row = ({ children }) => <div className="flex gap-3">{children}</div>;
const F1 = ({ label, v, onChange, testid, type = "text" }) => (
  <div className="py-1 flex-1">
    <div className="label-xs mb-0.5">{label}</div>
    <input className="input" value={v || ""} onChange={onChange} type={type} data-testid={testid} />
  </div>
);
